import { Pencil, Trash2, Mail, Cake } from "lucide-react";
import { CHARACTERS } from "@/data/characters";
import { PRODUCTS } from "@/data/catalog";

const fmt = (iso, lang) => new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { month: "long", day: "numeric" });
const TONE = { out: "text-[#C9876B]", soon: "text-[#C9A96B]", plenty: "text-[#8FB08A]", untried: "text-[#D3CEC4]/55" };

const statusLine = (s, t, lang) => {
  if (s.status === "untried") return t("Not yet in the cupboard. A good one to try.", "Pas encore dans l'armoire. Un bon choix à essayer.");
  if (s.status === "out") return t(`Likely empty since ${fmt(s.runout_at, lang)}.`, `Probablement vide depuis le ${fmt(s.runout_at, lang)}.`);
  if (s.status === "soon") return t(`Running low: about ${s.days_left} days left.`, `Bientôt vide : environ ${s.days_left} jours restants.`);
  return t(`Plenty left: lasts to about ${fmt(s.runout_at, lang)}.`, `Il en reste : jusqu'au ${fmt(s.runout_at, lang)} environ.`);
};

const Suggestion = ({ s, dogId, t, lang, onAdd }) => {
  const p = PRODUCTS.find((x) => x.id === s.product_id);
  if (!p) return null;
  const urgent = s.status === "soon" || s.status === "out" || s.status === "untried";
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3 border-t border-[#303B2D]/60" data-testid={`dog-suggestion-${dogId}-${p.id}`}>
      <div className="min-w-0">
        <p className="font-display text-lg text-[#E8DDC7]">{p.name}</p>
        <p className={`text-[13px] ${TONE[s.status]}`} data-testid={`dog-suggestion-status-${dogId}-${p.id}`}>{statusLine(s, t, lang)}</p>
        {s.shared_with?.length > 0 && (
          <p className="text-[12px] italic text-[#D3CEC4]/55" data-testid={`dog-suggestion-shared-${dogId}-${p.id}`}>
            {t(`Shared with ${s.shared_with.join(" & ")}, so we count them too.`, `Partagé avec ${s.shared_with.join(" et ")}, alors nous les comptons aussi.`)}
          </p>
        )}
      </div>
      <button onClick={() => onAdd(p.id)} data-testid={`dog-refill-${dogId}-${p.id}`}
        className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] rounded-sm transition-colors duration-300 ${urgent ? "bg-[#4B3226] text-[#E8DDC7] hover:bg-[#5d3f30]" : "border border-[#303B2D] text-[#D3CEC4]/70 hover:border-[#B5A642]/60"}`}>
        {s.status === "untried" ? t("Add to basket", "Ajouter au panier") : t("Refill", "Renouveler")}
      </button>
    </li>
  );
};

export const DogCard = ({ dog, t, lang, onEdit, onDelete, onAdd }) => {
  const titles = dog.characters.map((id) => CHARACTERS.find((c) => c.id === id)).filter(Boolean).map((c) => t(...c.title));
  return (
    <li className="border border-[#303B2D]/70 rounded-sm p-6" data-testid={`dog-card-${dog.dog_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-3xl text-[#E8DDC7]" data-testid={`dog-name-${dog.dog_id}`}>{dog.name}</h3>
          <p className="mt-1 font-display italic text-[#B5A642]/90">{titles.join(" · ")}</p>
          <p className="mt-1 font-ledger text-[12px] text-[#D3CEC4]/55">
            {dog.weight_lb} lb{dog.age_years != null && ` · ${dog.age_years} ${t("yrs", "ans")}`}
            {dog.email_notes && <span className="inline-flex items-center gap-1 ml-2"><Mail className="w-3 h-3" />{t("refill notes on", "rappels activés")}</span>}
          </p>
          {dog.birthday && (
            <p className="mt-1 inline-flex items-center gap-1.5 font-ledger text-[12px] text-[#D3CEC4]/55" data-testid={`dog-birthday-${dog.dog_id}`}>
              <Cake className="w-3 h-3 text-[#B5A642]" />
              {new Date(`${dog.birthday}T12:00:00`).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { month: "long", day: "numeric" })}
              {dog.birthday_letter && ` · ${t("birthday letter on", "lettre d'anniversaire activée")}`}
            </p>
          )}
        </div>
        <div className="flex gap-1">
          <button onClick={onEdit} aria-label={t("Edit", "Modifier")} data-testid={`dog-edit-${dog.dog_id}`} className="p-2 text-[#D3CEC4]/55 hover:text-[#E8DDC7] transition-colors"><Pencil className="w-4 h-4" /></button>
          <button onClick={onDelete} aria-label={t("Remove", "Retirer")} data-testid={`dog-delete-${dog.dog_id}`} className="p-2 text-[#D3CEC4]/55 hover:text-[#C9876B] transition-colors"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <p className="mt-5 text-[10px] uppercase tracking-[0.3em] text-[#B5A642]/80">{t("From the cabinet, for this one", "De l'armoire, pour celui-ci")}</p>
      <ul className="mt-2">{(dog.suggestions || []).map((s) => <Suggestion key={s.product_id} s={s} dogId={dog.dog_id} t={t} lang={lang} onAdd={onAdd} />)}</ul>
    </li>
  );
};
